import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, Brain, Loader2, Coins, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";

const QuizGenerator = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, refreshUser, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [creditCosts, setCreditCosts] = useState({ quiz_generation: 3 });
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const pollIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const hasCompletedRef = useRef(false);
  
  const selectedChapters = location.state?.selectedChapters || [];
  
  const [config, setConfig] = useState({
    detail_level: 50,
    num_questions: 10,
    include_multiple_choice: true,
    include_true_false: true
  });

  useEffect(() => {
    axios.get(`${API}/credits/costs`)
      .then(res => setCreditCosts(res.data))
      .catch(err => console.error("Failed to fetch costs:", err));
    
    return () => {
      if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  const calculateCredits = () => {
    return (creditCosts.quiz_generation || 3) * Math.ceil(config.num_questions / 10);
  };

  const startProgressAnimation = () => {
    setProgress(5);
    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        // Slowly increase progress, max out at 90% until complete
        if (prev < 30) return prev + 2;
        if (prev < 60) return prev + 1;
        if (prev < 90) return prev + 0.5;
        return prev;
      });
    }, 500);
  };

  const stopProgressAnimation = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const handleGenerate = async () => {
    if (selectedChapters.length === 0) {
      toast.error("No chapters selected. Please go back and select chapters.");
      navigate(`/books/${bookId}/chapters`);
      return;
    }

    const creditsNeeded = calculateCredits();
    if ((user?.credits || 0) < creditsNeeded) {
      toast.error("Insufficient credits");
      navigate("/pricing");
      return;
    }

    // Reset state
    setLoading(true);
    setProgress(0);
    setStatusMessage("Starting generation...");
    hasCompletedRef.current = false;
    
    try {
      const response = await axios.post(`${API}/quizzes/generate`, {
        book_id: bookId,
        chapter_ids: selectedChapters,
        ...config
      }, {
        headers: getAuthHeaders(token),
        timeout: 15000 // Short timeout - endpoint should return quickly
      });

      const quizId = response.data.quiz_id;
      setStatusMessage("Generating quiz questions...");
      startProgressAnimation();
      
      // Poll for status using recursive setTimeout (more reliable than setInterval)
      const pollStatus = async () => {
        // Prevent multiple completions
        if (hasCompletedRef.current) return;
        
        try {
          const statusRes = await axios.get(`${API}/quizzes/${quizId}`, {
            headers: getAuthHeaders(token)
          });
          
          console.log("Poll status:", statusRes.data.status);
          
          if (statusRes.data.status === "ready") {
            hasCompletedRef.current = true;
            stopProgressAnimation();
            setProgress(100);
            setStatusMessage("Complete!");
            await refreshUser();
            toast.success("Quiz generated successfully!");
            setTimeout(() => {
              navigate("/quizzes", { state: { newQuizId: quizId } });
            }, 500);
          } else if (statusRes.data.status === "error") {
            hasCompletedRef.current = true;
            stopProgressAnimation();
            setLoading(false);
            setProgress(0);
            toast.error(statusRes.data.error || "Generation failed. Please try again.");
          } else {
            // Still processing, poll again in 2 seconds
            pollIntervalRef.current = setTimeout(pollStatus, 2000);
          }
        } catch (err) {
          console.error("Error polling quiz status:", err);
          // On error, try again
          pollIntervalRef.current = setTimeout(pollStatus, 3000);
        }
      };
      
      // Start polling after a short delay
      pollIntervalRef.current = setTimeout(pollStatus, 2000);
      
      // Safety timeout: after 3 minutes, stop and show message
      setTimeout(() => {
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
          stopProgressAnimation();
          setLoading(false);
          setProgress(0);
          toast.info("Taking longer than expected. Check your quizzes to see if generation completed.");
        }
      }, 180000); // 3 minutes

    } catch (error) {
      stopProgressAnimation();
      setLoading(false);
      setProgress(0);
      
      if (error.code === 'ECONNABORTED') {
        toast.error("Request timed out. Please try again.");
      } else if (error.response?.status === 402) {
        toast.error("Insufficient credits");
        navigate("/pricing");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate quiz");
      }
    }
  };

  if (selectedChapters.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <main className="container-main py-8">
          <Card className="card-base max-w-md mx-auto text-center">
            <CardContent className="py-12">
              <p className="text-slate-600 mb-4">No chapters selected</p>
              <Button onClick={() => navigate(`/books/${bookId}/chapters`)} className="btn-primary">
                Select Chapters
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quiz-generator-page">
      <DashboardHeader />

      <main className="container-main py-8">
        <Button 
          variant="ghost" 
          className="mb-6 gap-2"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        <div className="max-w-2xl mx-auto">
          <Card className="card-base">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl flex items-center justify-center">
                  <Brain className="w-6 h-6 text-[#F97316]" />
                </div>
                <div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Generate Learning Quiz
                  </CardTitle>
                  <CardDescription>
                    {selectedChapters.length} chapter{selectedChapters.length > 1 ? 's' : ''} selected
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-8">
              {/* Info Box */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 flex gap-3">
                <Sparkles className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900 mb-1">Learning Mode</p>
                  <p className="text-sm text-slate-600">
                    Quizzes are designed for learning with detailed explanations after each question. 
                    Take them online and download your results when finished.
                  </p>
                </div>
              </div>

              {/* Detail Level */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Explanation Detail Level</Label>
                  <span className="text-sm font-medium text-[#F97316]">
                    {config.detail_level < 33 ? 'Brief' : config.detail_level < 66 ? 'Standard' : 'Detailed'}
                  </span>
                </div>
                <Slider
                  value={[config.detail_level]}
                  onValueChange={(value) => setConfig({ ...config, detail_level: value[0] })}
                  max={100}
                  step={1}
                  className="py-2"
                  data-testid="detail-slider"
                  disabled={loading}
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Brief</span>
                  <span>Standard</span>
                  <span>Detailed</span>
                </div>
              </div>

              {/* Number of Questions */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Number of Questions</Label>
                <Select
                  value={config.num_questions.toString()}
                  onValueChange={(value) => setConfig({ ...config, num_questions: parseInt(value) })}
                  disabled={loading}
                >
                  <SelectTrigger className="h-12" data-testid="num-questions-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20].map(num => (
                      <SelectItem key={num} value={num.toString()}>
                        {num} questions
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Question Types */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Question Types</Label>
                
                <div className="space-y-4 bg-slate-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={config.include_multiple_choice}
                      onCheckedChange={(checked) => setConfig({ ...config, include_multiple_choice: checked })}
                      data-testid="multiple-choice-switch"
                      disabled={loading}
                    />
                    <span>Multiple Choice (Key Concepts)</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={config.include_true_false}
                      onCheckedChange={(checked) => setConfig({ ...config, include_true_false: checked })}
                      data-testid="true-false-switch"
                      disabled={loading}
                    />
                    <span>True / False (Important Facts)</span>
                  </div>
                </div>
              </div>

              {/* Cost Summary */}
              <div className="bg-orange-50 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Coins className="w-5 h-5 text-[#F97316]" />
                  <span className="text-slate-700">Credit Cost</span>
                </div>
                <span className="font-bold text-lg text-[#F97316]">{calculateCredits()} credits</span>
              </div>

              {/* Progress indicator when generating */}
              {loading && (
                <div className="space-y-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">{statusMessage}</span>
                    <span className="text-orange-600 font-medium">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-slate-500">
                    This may take up to a minute depending on the number of questions.
                  </p>
                </div>
              )}

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={loading || (!config.include_multiple_choice && !config.include_true_false)}
                className="w-full btn-accent h-14 text-lg"
                data-testid="generate-quiz-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Brain className="w-5 h-5 mr-2" />
                    Generate Quiz
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default QuizGenerator;
